import { Stack, Text, Title } from "@mantine/core";
import { useMemo } from "react";
import {
  DictionaryManagement,
  DictionaryService,
} from "../../components/feature-specific/DictionaryManagement";
import { useLanguageProficiencies } from "../../hooks/queries/useLanguageDictionary";
import {
  ConsolidateProficienciesRequest,
  Proficiency,
  ProficiencyCreateRequest,
  ProficiencyUpdateRequest,
} from "../../models/language.model";
import { LanguageDictionaryAdminService } from "../../services/admin";

export function ProficiencyDictionary() {
  const languageService = useMemo(
    () => new LanguageDictionaryAdminService(),
    []
  );

  // Use React Query for reading proficiencies
  const { data: proficienciesFromQuery = [] } = useLanguageProficiencies();

  // Adapt LanguageDictionaryAdminService to DictionaryService interface for proficiencies
  const dictionaryService: DictionaryService<Proficiency> = useMemo(
    () => ({
      getDictionary: async () => proficienciesFromQuery,
      createItem: (item: Omit<Proficiency, "id">) =>
        languageService.createProficiency({
          level: item.level,
        } as ProficiencyCreateRequest),
      updateItem: (id: number, item: Partial<Proficiency>) =>
        languageService.updateProficiency(id, {
          id,
          level: item.level!,
        } as ProficiencyUpdateRequest),
      deleteItem: (id: number) => languageService.deleteProficiency(id),
      consolidateItems: (sourceId: number, targetId: number) =>
        languageService.consolidateProficiencies({
          sourceProficiencyId: sourceId,
          targetProficiencyId: targetId,
        } as ConsolidateProficienciesRequest),
    }),
    [languageService, proficienciesFromQuery]
  );

  return (
    <Stack gap="md">
      <Stack gap="xs" mb="md">
        <Title order={3} size="h4" fw={600}>
          Proficiency Levels
        </Title>
        <Text c="dimmed">
          Manage language proficiency levels used throughout the system
        </Text>
      </Stack>

      <DictionaryManagement
        title="Proficiency Levels"
        service={dictionaryService}
        itemDisplayName="Proficiency Level"
        itemDisplayNamePlural="Proficiency Levels"
        canConsolidate={true}
        titleFieldName="level"
        searchPlaceholder="Search proficiency levels..."
      />
    </Stack>
  );
}
